// bluaIQ — Gantt generic blocker display vocabulary (Phase 1)
//
// DISPLAY-ONLY. Nothing here is persisted, enforced, propagated, or derived
// from another module. There is no Material Orders, Bids, Documents, Contracts,
// Insurance, Payments, or backend read in this file. There is no jurisdiction
// or location logic here — those layers come later, only once a project address
// is known. These helpers exist so the Gantt UI can render a clear, bilingual
// "why is this task blocked / what is recommended" surface from data the task
// already carries (status, notes, requiredUploads, startDate).
//
// Pure functions only — safe to unit-test without React.

// ── Severity (display-only classification) ─────────────────────────
// active        = would prevent a task from being ready to start
// warning       = does not prevent start, should be reviewed
// informational = context only
export type BlockerSeverity = 'active' | 'warning' | 'informational'

export function blockerSeverityLabel(severity: BlockerSeverity, isEs: boolean): string {
  switch (severity) {
    case 'active':
      return isEs ? 'Bloqueo activo' : 'Active blocker'
    case 'warning':
      return isEs ? 'Advertencia' : 'Warning'
    case 'informational':
      return isEs ? 'Informativo' : 'Informational'
    default:
      return severity
  }
}

// ── Generic blocker reason vocabulary (display-only, not persisted) ──
// A future schema PR may add a typed blockerReason column. Until then this
// is purely a label dictionary the UI can use; nothing reads or writes it
// to the backend.
export const BLOCKER_REASON_LABELS: Record<string, { en: string; es: string }> = {
  PERMIT_PENDING: { en: 'Permit pending', es: 'Permiso pendiente' },
  PLAN_APPROVAL_MISSING: { en: 'Plan approval missing', es: 'Falta aprobación de planos' },
  DOCUMENT_MISSING: { en: 'Required document missing', es: 'Falta documento requerido' },
  DOCUMENT_EXPIRED: { en: 'Document expired', es: 'Documento vencido' },
  DOCUMENT_REJECTED: { en: 'Document rejected', es: 'Documento rechazado' },
  SITE_ACCESS_ISSUE: { en: 'Site access issue', es: 'Problema de acceso al sitio' },
  UTILITY_LOCATE_PENDING: { en: 'Utility locate pending', es: 'Localización de servicios pendiente' },
  WEATHER_DELAY: { en: 'Weather delay', es: 'Retraso por clima' },
  MATERIAL_NOT_ORDERED: { en: 'Material not ordered', es: 'Material no pedido' },
  MATERIAL_NOT_DELIVERED: { en: 'Material not delivered', es: 'Material no entregado' },
  LONG_LEAD_NOT_ORDERED: { en: 'Long-lead item not ordered early', es: 'Material de entrega larga no pedido a tiempo' },
  SUPPLIER_QUOTE_PENDING: { en: 'Supplier quote pending', es: 'Cotización del proveedor pendiente' },
  SUBCONTRACTOR_NOT_ASSIGNED: { en: 'Subcontractor not assigned', es: 'Subcontratista no asignado' },
  CONTRACT_NOT_SIGNED: { en: 'Contract not signed', es: 'Contrato sin firmar' },
  INSURANCE_MISSING: { en: 'Insurance / COI missing', es: 'Falta seguro / COI' },
  INSURANCE_EXPIRED: { en: 'Insurance expired', es: 'Seguro vencido' },
  LICENSE_NOT_VERIFIED: { en: 'License not verified', es: 'Licencia no verificada' },
  DEPOSIT_REQUIRED: { en: 'Deposit required', es: 'Depósito requerido' },
  PAYMENT_PENDING: { en: 'Payment pending', es: 'Pago pendiente' },
  DRAW_NOT_RELEASED: { en: 'Draw / payment not released', es: 'Pago / draw no liberado' },
  CHANGE_ORDER_PENDING: { en: 'Change order pending', es: 'Orden de cambio pendiente' },
  OWNER_DECISION_PENDING: { en: 'Owner decision pending', es: 'Decisión del propietario pendiente' },
  SELECTION_PENDING: { en: 'Selection pending', es: 'Selección pendiente' },
  HOA_APPROVAL_MISSING: { en: 'HOA approval missing', es: 'Falta aprobación de HOA' },
  INSPECTION_PENDING: { en: 'Inspection pending', es: 'Inspección pendiente' },
  INSPECTION_FAILED: { en: 'Inspection failed', es: 'Inspección reprobada' },
  CORRECTION_REQUIRED: { en: 'Correction required', es: 'Corrección requerida' },
  UTILITY_CONNECTION_PENDING: { en: 'Utility connection pending', es: 'Conexión de servicios pendiente' },
  OTHER: { en: 'Other', es: 'Otro' },
}

export function blockerReasonLabel(reason: string, isEs: boolean): string {
  const entry = BLOCKER_REASON_LABELS[reason]
  if (!entry) return reason
  return isEs ? entry.es : entry.en
}

// ── Generic blocker source vocabulary (display-only) ────────────────
// Where a blocker would originate, once cross-module wiring exists. Today
// nothing here is read from those modules — it is only a label dictionary.
export const BLOCKER_SOURCE_LABELS: Record<string, { en: string; es: string }> = {
  TASK_DEPENDENCY: { en: 'Task dependency', es: 'Dependencia de tarea' },
  DOCUMENTS: { en: 'Documents', es: 'Documentos' },
  BIDS: { en: 'Bids', es: 'Cotizaciones' },
  CONTRACTS: { en: 'Contracts', es: 'Contratos' },
  INSURANCE: { en: 'Insurance', es: 'Seguros' },
  MATERIAL_ORDERS: { en: 'Material orders', es: 'Pedidos de material' },
  PAYMENTS: { en: 'Payments', es: 'Pagos' },
  CALENDAR: { en: 'Calendar', es: 'Calendario' },
  MANUAL: { en: 'Manual', es: 'Manual' },
}

export function blockerSourceLabel(source: string, isEs: boolean): string {
  const entry = BLOCKER_SOURCE_LABELS[source]
  if (!entry) return source
  return isEs ? entry.es : entry.en
}

// ── Section headings (bilingual) ────────────────────────────────────
export function blockerReasonHeading(isEs: boolean): string {
  return isEs ? 'Motivo del bloqueo' : 'Blocker reason'
}

export function requiredItemsHeading(isEs: boolean): string {
  return isEs ? 'Requisitos pendientes' : 'Required items'
}

// ── Blocker reason text from a task's notes ─────────────────────────
// Reads the task's own free-text notes as a temporary blocker reason. The
// param is a structural type so a GanttTask (which does not currently carry
// a `notes` field, because GET /tasks does not select it yet) can be passed
// safely — `notes` simply resolves to undefined and the honest fallback is
// shown. No backend read, no inference about other modules.
export function blockerReasonText(task: { notes?: string | null }, isEs: boolean): string {
  const note = (task.notes || '').trim()
  if (note) return note
  return isEs ? 'No se especificó motivo del bloqueo' : 'No blocker reason provided'
}

// ── Generic material lead-time advisory (recommendation, not a fact) ─
// Worded as planning guidance only. This does NOT read Material Orders, does
// NOT know whether anything was actually ordered or delivered, and creates no
// ready-to-order / SKU / pricing logic. It is a generic reminder that for
// trade work, material is typically ordered roughly two weeks ahead.
export const DEFAULT_MATERIAL_LEAD_DAYS = 14

export function materialLeadTimeAdvisory(isEs: boolean): string {
  return isEs
    ? 'Planeación de material: pedir aprox. 2 semanas antes de esta tarea.'
    : 'Material planning: order about 2 weeks before this task.'
}

// Optional helper: a recommended order-by date derived purely from the task's
// own start date and the generic default lead time. Returns null when the
// start date is missing or unparseable (never throws). This is advisory only.
export function materialOrderByDate(
  startDate?: string | null,
  leadDays: number = DEFAULT_MATERIAL_LEAD_DAYS,
): string | null {
  if (!startDate) return null
  const ms = new Date(startDate).getTime()
  if (isNaN(ms)) return null
  const d = new Date(ms)
  d.setDate(d.getDate() - leadDays)
  return d.toISOString().slice(0, 10)
}
